/**
 * AppRoutes Component - Centralized Routing Configuration
 *
 * This component provides a centralized place to define all your application routes.
 *
 * Usage in App.tsx:
 *
 * import AppRoutes from '@/routes/AppRoutes';
 *
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <AppRoutes />
 *     </BrowserRouter>
 *   );
 * }
 *
 * TODO: Customize routes based on your application structure
 */

import { Routes, Route, Navigate } from 'react-router-dom'
import PrivateRoute from '@/routes/PrivateRoute'
import Layout from '@/components/Layout'

// Public pages
import Login from '@/pages/Login'

// Protected pages
import AssortmentPlans from '@/pages/AssortmentPlans'
import AssortmentPlanDetail from '@/pages/AssortmentPlanDetail'
import Dashboard from '@/pages/Dashboard'
import ExampleForm from '@/pages/ExampleForm'
import Admin from '@/pages/Admin'
import AdminUsers from '@/pages/AdminUsers'
import AdminDocumentTemplates from '@/pages/AdminDocumentTemplates'
import AdminAttributes from '@/pages/AdminAttributes'
import AdminStoreModels from '@/pages/AdminStoreModels'
import AdminStoreModelCreate from '@/pages/AdminStoreModelCreate'
import AdminStoreModelDetail from '@/pages/AdminStoreModelDetail'
import AdminStores from '@/pages/AdminStores'
import Notifications from '@/pages/Notifications'
import Approvals from '@/pages/Approvals'
import ApprovalDetail from '@/pages/ApprovalDetail'
import AdminCalendars from '@/pages/AdminCalendars'
import AdminCalendarDetail from '@/pages/AdminCalendarDetail'
import AdminImages from '@/pages/AdminImages'
import AdminAssortmentAccess from '@/pages/AdminAssortmentAccess'
import AdminDropdowns from '@/pages/AdminDropdowns'
import AdminWarehouseLookups from '@/pages/AdminWarehouseLookups'
import AdminSystemSettings from '@/pages/AdminSystemSettings'
// TODO: Import your other protected pages here
// import Profile from '@/pages/Profile';
// import Settings from '@/pages/Settings';
// import Users from '@/pages/Users';
// etc.

export default function AppRoutes() {
  return (
    <Routes>
      {/* ============================================ */}
      {/* PUBLIC ROUTES - No authentication required */}
      {/* ============================================ */}

      {/* TODO: Adjust paths based on your app's routing structure */}
      <Route path="/login" element={<Login />} />

      {/* TODO: Add other public routes if needed */}
      {/* <Route path="/signup" element={<Signup />} /> */}
      {/* <Route path="/forgot-password" element={<ForgotPassword />} /> */}
      {/* <Route path="/reset-password" element={<ResetPassword />} /> */}
      {/* <Route path="/verify-email" element={<VerifyEmail />} /> */}
      {/* <Route path="/terms" element={<Terms />} /> */}
      {/* <Route path="/privacy" element={<Privacy />} /> */}
      {/* <Route path="/contact" element={<Contact />} /> */}

      {/* ============================================ */}
      {/* PROTECTED ROUTES - Authentication required  */}
      {/* ============================================ */}

      {/* 
        All routes inside PrivateRoute wrapper are protected.
        Users will be redirected to LANDING_URL (default: /login) if not authenticated.
        
        The access token is automatically:
        - Added to all API requests via Axios interceptors
        - Refreshed proactively before expiration (10 seconds margin)
        - Re-attempted on 401 errors (with automatic refresh + retry)
      */}
      <Route
        path="*"
        element={
          <PrivateRoute>
            <Layout>
              <Routes>
                {/* Main routes */}
                <Route path="/" element={<AssortmentPlans />} />
                <Route
                  path="/dashboard"
                  element={<Navigate to="/dashboard/cat" replace />}
                />
                <Route path="/dashboard/:workspace" element={<Dashboard />} />
                <Route
                  path="/plans/:planId"
                  element={<AssortmentPlanDetail />}
                />
                <Route path="/form" element={<ExampleForm />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route
                  path="/admin/assortment-access"
                  element={<AdminAssortmentAccess />}
                />
                <Route
                  path="/admin/document-templates"
                  element={<AdminDocumentTemplates />}
                />
                <Route
                  path="/admin/sku-attributes"
                  element={<AdminAttributes />}
                />
                <Route path="/store-models" element={<AdminStoreModels />} />
                <Route
                  path="/store-models/create"
                  element={<AdminStoreModelCreate />}
                />
                <Route
                  path="/store-models/:modelId"
                  element={<AdminStoreModelDetail />}
                />

                <Route path="/admin/stores" element={<AdminStores />} />
                <Route path="/images" element={<AdminImages />} />
                <Route path="/admin/calendars" element={<AdminCalendars />} />
                <Route
                  path="/admin/calendars/:calendarId"
                  element={<AdminCalendarDetail />}
                />
                <Route path="/admin/dropdowns" element={<AdminDropdowns />} />
                <Route
                  path="/admin/warehouse-lookups"
                  element={<AdminWarehouseLookups />}
                />
                <Route
                  path="/admin/system-settings"
                  element={<AdminSystemSettings />}
                />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/approvals" element={<Approvals />} />
                <Route
                  path="/approvals/:submissionId"
                  element={<ApprovalDetail />}
                />

                {/* TODO: Add your other protected routes here */}
                {/* Example: User-related routes */}
                {/* <Route path="/profile" element={<Profile />} /> */}
                {/* <Route path="/profile/edit" element={<EditProfile />} /> */}
                {/* <Route path="/settings" element={<Settings />} /> */}

                {/* Example: Admin routes */}
                {/* <Route path="/users" element={<Users />} /> */}
                {/* <Route path="/users/:id" element={<UserDetail />} /> */}
                {/* <Route path="/roles" element={<Roles />} /> */}

                {/* Example: Feature-specific routes */}
                {/* <Route path="/reports" element={<Reports />} /> */}
                {/* <Route path="/analytics" element={<Analytics />} /> */}
                {/* <Route path="/notifications" element={<Notifications />} /> */}

                {/* Example: 404 page for authenticated users */}
                {/* <Route path="*" element={<NotFound />} /> */}
              </Routes>
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  )
}

/**
 * NOTES:
 *
 * 1. Route Organization:
 *    - Keep public routes at the top for clarity
 *    - Group protected routes logically (by feature, role, etc.)
 *
 * 2. Route Protection:
 *    - Only wrap routes that need authentication with <PrivateRoute>
 *    - Public routes (login, signup, etc.) should NOT be wrapped
 *
 * 3. Nested Routes:
 *    - You can create nested route structures as needed
 *    - Example: /admin/users, /admin/settings, etc.
 *
 * 4. Dynamic Routes:
 *    - Use :param syntax for dynamic segments
 *    - Example: /users/:userId, /posts/:postId/edit
 *
 * 5. Lazy Loading (Optional):
 *    - For better performance, consider lazy loading pages
 *    - Example: const Dashboard = lazy(() => import('@/pages/Dashboard'));
 *    - Wrap <Routes> with <Suspense fallback={<Loading />}>
 */
